import React, { useCallback, useEffect, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import AppSelect from "@/features/line-sheet-sets/app-select";
import PathPickAutocompleteInput from "@/features/pathpick/components/ui/PathPickAutocompleteInput";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickSearchProductsProductListFilter from "@/features/pathpick/features/search-products/components/product-list/PathPickSearchProductsProductListFilter";
import usePathPickProductFilter from "@/features/pathpick/features/search-products/hooks/usePathPickProductFilter";
import usePathPickCollectionOptions from "@/features/pathpick/features/search-products/hooks/usePathPickCollectionOptions";
import {
  KreamBuyerInfo,
  PathPickProductListViewType,
} from "@/features/pathpick/features/search-products/types";
import {
  PathPickHotDealLineSheet,
  PathPickMarketPriceBasis,
  PathPickProductOrderBy,
} from "@/features/pathpick/types";
import PathPickSearchProductsKreamBuyerInfoModal from "@/features/pathpick/features/search-products/components/product-list/PathPickSearchProductsKreamBuyerInfoModal";
import PathPickProductCardViewSettingsButton from "@/features/pathpick/components/product/PathPickProductCardViewSettingsButton";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import PathPickSelectAllResultsCheckBox from "@/features/pathpick/components/ui/PathPickSelectAllResultsCheckBox";
import PathPickFilterToggleButton from "@/features/pathpick/components/ui/PathPickFilterToggleButton";
import PathPickSearchProductsProductListFilterModal, {
  countPathPickProductFilterAxes,
} from "@/features/pathpick/features/search-products/components/product-list/PathPickSearchProductsProductListFilterModal";
import usePathPickProductSortOptions from "@/features/pathpick/features/search-products/hooks/usePathPickProductSortOptions";

interface Props {
  viewType: PathPickProductListViewType;
  onChangeViewType: (viewType: PathPickProductListViewType) => void;
  // 현재 페이지에 고를 수 있는(아직 담기지 않은) 상품이 있는지 — 없으면 이 페이지 선택을 비활성화한다.
  isPageSelectable: boolean;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  // 페이지를 넘어 선택된 전체 수 — 담기는 이 전부를 담는다.
  selectedCount: number;
  // 미리 받은 전체 선택 대상(ids_only)이 있을 때만 전체 선택을 노출한다.
  canSelectAllResults: boolean;
  // 전체 선택 대상 수 — ids_only 상한(1만 건)에서 멈춘다.
  allResultsCount: number;
  // 전체 선택 대상이 상한에서 잘렸는지 — 라벨을 "최대 n개 선택"으로 바꾼다.
  isAllResultsTruncated: boolean;
  isAllResultsSelected: boolean;
  isAllResultsIndeterminate: boolean;
  isFetchingAllResults: boolean;
  onTogglePage: () => void;
  onToggleAllResults: () => void;
  onBulkPick: () => void;
  selectedLineSheet: PathPickHotDealLineSheet | null;
  onClearSelectedLineSheet: () => void;
  marketPriceBasis: PathPickMarketPriceBasis;
  onChangeMarketPriceBasis: (basis: PathPickMarketPriceBasis) => void;
  kreamBuyerInfo: KreamBuyerInfo;
  onSaveKreamBuyerInfo: (buyerInfo: KreamBuyerInfo) => void;
}

function PathPickSearchProductsProductListHeader({
  viewType,
  onChangeViewType,
  isPageSelectable,
  isAllSelected,
  isIndeterminate,
  selectedCount,
  canSelectAllResults,
  allResultsCount,
  isAllResultsTruncated,
  isAllResultsSelected,
  isAllResultsIndeterminate,
  isFetchingAllResults,
  onTogglePage,
  onToggleAllResults,
  onBulkPick,
  selectedLineSheet,
  onClearSelectedLineSheet,
  marketPriceBasis,
  onChangeMarketPriceBasis,
  kreamBuyerInfo,
  onSaveKreamBuyerInfo,
}: Props) {
  const { t, tTitle } = useI18nHelper();
  const {
    filter,
    apply,
    applyQuery,
    toggleCollection,
    clearCollections,
    resetComparisonFilters,
  } = usePathPickProductFilter();
  // 비교 표시를 끄면 비교와 관련된 조회 조건(매칭 상품 유형/범위 필터/비교 기준/마진 정렬)을 함께 감춘다 —
  // 카드에 보이지도 않는 값으로 목록을 거르거나 정렬하게 두면 결과를 설명할 수 없다.
  const { isComparisonVisible } = usePathPickProductCardView();
  const collectionOptions = usePathPickCollectionOptions();
  const [searchValue, setSearchValue] = useState<string>(filter.query ?? "");
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  // 상세 필터는 memo로 끊겨 있어 닫기 콜백 참조가 고정돼야 한다.
  const closeFilter = useCallback(() => setIsFilterOpen(false), []);
  const [isKreamBuyerInfoModalOpen, setIsKreamBuyerInfoModalOpen] =
    useState<boolean>(false);
  // 모바일에서는 상품 유형·표시·정렬 줄과 상세 필터 펼침 대신 필터 모달 하나로 고른다.
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState<boolean>(false);

  // 쿼리스트링(query)이 외부에서 바뀌면(초기화/전체 해제 등) 입력창을 동기화한다.
  // (입력창 글자 자체는 자동완성 컴포넌트가 가지고, 여기 searchValue 는 검색 버튼용 사본이다)
  useEffect(() => {
    setSearchValue(filter.query ?? "");
  }, [filter.query]);

  const handleSearch = () => {
    applyQuery(searchValue.trim() || undefined);
  };

  // 자동완성에서 브랜드를 고르면 브랜드 필터에 더하고 검색어는 비운다 — 검색어까지 남기면
  // "브랜드 AND 검색어"로 좁혀져 고른 브랜드의 상품이 안 보인다. (제안 브랜드는 filter-info 범위라
  // 필터 패널에서 해제할 수 있다.)
  const handleSelectBrand = (brandId: number) => {
    const current = filter.brandId__in ?? [];
    apply({
      ...filter,
      brandId__in: current.includes(brandId) ? current : [...current, brandId],
      query: undefined,
      // 브랜드 필터를 직접 바꾸면 "즐겨찾기 = 브랜드 필터"라는 즐겨찾기 탭의 전제가 깨지므로 전체 탭으로.
      tab: filter.tab === "favoriteBrand" ? undefined : filter.tab,
    });
  };

  // SKU 를 고르면 그 값으로 바로 검색한다(원본 SKU — 서버가 기호를 지우고 찾는다).
  const handleSelectSku = (sku: string) => {
    applyQuery(sku);
  };

  const kreamBuyerInfoLabel = t("path_pick:filter.kream_buyer_info_value", {
    level: kreamBuyerInfo.level,
    productType: t(
      `path_pick:filter.kream_product_type_${kreamBuyerInfo.productType}`
    ),
  });

  // 상단 상품 유형 버튼 — 선택이 없으면 `전체`가 활성이다.
  const selectedCollections = filter.collection__in ?? [];

  // 정렬 — 새로고침에도 유지되도록 쿼리스트링(orderBy)으로 관리한다. 디폴트(추천순)도 항상 명시.
  const sortOptions = usePathPickProductSortOptions();

  const handleSelectOrderBy = (value?: PathPickProductOrderBy) => {
    apply({ ...filter, orderBy: value ?? "recommended" });
  };

  return (
    <>
      <Flex flexDirection={"column"} gap={"16px"}>
        <Text textStyle={"body-lg-bold"}>
          {t("path_pick:search_products.product_list.title")}
        </Text>
        <Flex flexDirection={"column"} gap={"8px"}>
          {selectedLineSheet && (
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              padding={"12px 16px"}
              border={"1px solid #1272EF"}
              borderRadius={"8px"}
              background={"#F0F7FF"}
              gap={"16px"}
            >
              <Flex flexDirection={"column"} gap={"2px"}>
                <Text textStyle={"caption-medium"} color={"#1272EF"}>
                  {t(
                    "path_pick:search_products.product_list.selected_line_sheet"
                  )}
                </Text>
                <Text textStyle={"body-lg-bold"} color={"#1D1D1B"}>
                  {selectedLineSheet.name}
                </Text>
              </Flex>
              <Flex gap={"8px"} alignItems={"center"}>
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t(
                    "path_pick:search_products.product_list.selected_line_sheet_note",
                    { count: selectedLineSheet.productCount }
                  )}
                </Text>
                <AppButton variant={"white"} onClick={onClearSelectedLineSheet}>
                  <Text textStyle={"body-sm-bold"}>
                    {t(
                      "path_pick:search_products.product_list.view_all_products"
                    )}
                  </Text>
                </AppButton>
              </Flex>
            </Flex>
          )}
          {/* 조회 조건 줄 — 검색어 입력만 둔다(상품 유형·상세 필터·표시·정렬 옵션은 아래 줄).
              모바일에서는 검색 버튼 없이(키보드 검색으로 제출) 검색창 옆에 필터 버튼을 붙인다. */}
          {/* stretch — 필터 버튼이 검색창 실제 높이를 따라간다. 모바일 기기에서는 전역 CSS
              (globals.css의 body.is-mobile-device input min-height: 40px)가 입력칸을 키운다. */}
          <Flex gap={"8px"} alignItems={"stretch"}>
            {/* 검색창의 width는 문자열만 받아 반응형 폭은 바깥 Box가 맡는다.
                모바일에서는 필터 버튼을 뺀 나머지 폭을 검색창이 다 쓴다. */}
            <Box
              flex={{ base: 1, md: "none" }}
              minWidth={0}
              width={{ base: "auto", md: "350px" }}
            >
              <PathPickAutocompleteInput
                width={"100%"}
                externalValue={filter.query}
                brandIds={filter.brandId__in}
                onChange={setSearchValue}
                onSubmit={handleSearch}
                onSelectBrand={handleSelectBrand}
                onSelectSku={handleSelectSku}
                placeholder={t(
                  "path_pick:search_products.product_list.search_placeholder"
                )}
                inputProps={{
                  border: "0.5px solid #C0C0C0",
                  _hover: {
                    borderColor: "#1272EF",
                  },
                  height: "32px",
                  // 모바일 키보드의 엔터 키를 `검색`으로 보여준다 — 모바일엔 검색 버튼이 없다.
                  enterKeyHint: "search",
                }}
              />
            </Box>
            {/* AppButton은 Tooltip으로 감싸여 있어 display 분기는 바깥 Box에 건다. */}
            <Box display={{ base: "none", md: "block" }} alignSelf={"center"}>
              <AppButton
                variant={"primary"}
                onClick={handleSearch}
                width={"80px"}
              >
                <Flex gap={"4px"} alignItems={"center"}>
                  <CustomIcon
                    src={"search"}
                    width={16}
                    height={16}
                    color={"#FFFFFF"}
                  />
                  <Text textStyle={"body-sm-bold"}>
                    {tTitle("common:search")}
                  </Text>
                </Flex>
              </AppButton>
            </Box>
            <PathPickFilterToggleButton
              appliedCount={countPathPickProductFilterAxes(
                filter,
                isComparisonVisible
              )}
              onClick={() => setIsMobileFilterOpen(true)}
              height={"auto"}
            />
            {/*<Divider*/}
            {/*  orientation={"vertical"}*/}
            {/*  background={"#C0C0C0"}*/}
            {/*  height={"22px"}*/}
            {/*  width={"1px"}*/}
            {/*/>*/}
            {/*<AppButton*/}
            {/*  variant={"white"}*/}
            {/*  onClick={() => setIsKreamBuyerInfoModalOpen(true)}*/}
            {/*  width={"220px"}*/}
            {/*>*/}
            {/*  <Flex gap={"6px"} alignItems={"center"}>*/}
            {/*    <CustomIcon*/}
            {/*      src={"user"}*/}
            {/*      width={16}*/}
            {/*      height={16}*/}
            {/*      color={"#444440"}*/}
            {/*    />*/}
            {/*    <Text textStyle={"body-sm-bold"}>*/}
            {/*      {t("path_pick:filter.kream_buyer_info_button")}*/}
            {/*    </Text>*/}
            {/*    <Text*/}
            {/*      textStyle={"caption-bold"}*/}
            {/*      color={"#1272EF"}*/}
            {/*      padding={"1px 8px"}*/}
            {/*      borderRadius={"6px"}*/}
            {/*      border={"0.5px solid #D4D4D1"}*/}
            {/*      background={"#F7F9FA"}*/}
            {/*    >*/}
            {/*      {kreamBuyerInfoLabel}*/}
            {/*    </Text>*/}
            {/*  </Flex>*/}
            {/*</AppButton>*/}
          </Flex>
          {/* 상품 유형 줄 — 버튼은 누르는 즉시 반영되고 여러 개를 함께 고를 수 있다(합집합).
              상세 필터의 상품 유형 섹션과 같은 값이라 서로 선택이 따라간다.
              오른쪽 끝에는 표시·정렬 옵션을 붙인다.
              모바일에서는 이 줄 전체를 숨기고 같은 조건을 필터 모달에서 고른다. */}
          <Flex
            display={{ base: "none", md: "flex" }}
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={"8px"}
          >
            <Flex gap={"8px"} alignItems={"center"}>
              <AppButton
                variant={"white"}
                size={"medium"}
                isActive={selectedCollections.length === 0}
                onClick={clearCollections}
                // 오른쪽 표시·정렬 옵션 버튼과 같은 기본 높이(28px)를 유지하도록 세로 패딩은 그대로 둔다.
                chakraProps={{
                  padding: "4px 8px",
                  flexShrink: 0,
                }}
              >
                <Text textStyle={"body-sm-medium"} whiteSpace={"nowrap"}>
                  {tTitle("common:all")}
                </Text>
              </AppButton>
              {collectionOptions.map((option) => {
                const isSelected = selectedCollections.includes(option.value);
                return (
                  // AppCheckBox가 자체 버튼을 그려 AppButton 안에 넣을 수 없으므로 카드 모양 Flex로 감싸고,
                  // 클릭은 바깥에서 받는다(체크박스에 onChange를 주면 두 번 토글된다).
                  <Flex
                    key={option.value}
                    role={"checkbox"}
                    aria-checked={isSelected}
                    alignItems={"center"}
                    height={"28px"}
                    paddingX={"10px"}
                    flexShrink={0}
                    cursor={"pointer"}
                    border={
                      isSelected ? "0.5px solid #1272EF" : "0.5px solid #8F8F8C"
                    }
                    borderRadius={"4px"}
                    background={"#FCFCFB"}
                    boxShadow={"1px 2px 4px 0px rgba(0, 0, 0, 0.1)"}
                    _hover={{ borderColor: "#1272EF" }}
                    onClick={() => toggleCollection(option.value)}
                  >
                    <AppCheckBox
                      size={"small"}
                      isChecked={isSelected}
                      name={
                        option.value === "HOT_DEAL"
                          ? `🔥 ${option.label}`
                          : option.label
                      }
                    />
                  </Flex>
                );
              })}
              <AppButton
                variant={"primary"}
                onClick={() => setIsFilterOpen((prev) => !prev)}
                // 기본(medium) 좌우 패딩 32px은 이 줄에서 너무 넓어 줄인다.
                chakraProps={{ padding: "4px 12px", flexShrink: 0 }}
              >
                <Flex gap={"4px"} alignItems={"center"}>
                  <Text textStyle={"body-sm-bold"} whiteSpace={"nowrap"}>
                    {t("path_pick:filter.detail")}
                  </Text>
                  <CustomIcon
                    src={isFilterOpen ? "chevron-up" : "chevron-down"}
                    width={16}
                    height={16}
                    color={"#FFFFFF"}
                  />
                </Flex>
              </AppButton>
            </Flex>
            {/* 표시·정렬 옵션 — 상품 유형 버튼 묶음 오른쪽 끝에 붙인다. */}
            <Flex gap={"8px"} alignItems={"center"} flexShrink={0}>
              {isComparisonVisible && (
                <Flex gap={"4px"} alignItems={"center"}>
                  <Text
                    textStyle={"body-sm-bold"}
                    color={"#444440"}
                    marginRight={"4px"}
                  >
                    {t("path_pick:filter.market_basis")}
                  </Text>
                  <AppButton
                    variant={"white"}
                    isActive={marketPriceBasis === "bidPrice"}
                    onClick={() => onChangeMarketPriceBasis("bidPrice")}
                    // AppButton의 width는 문자열 전용이라 반응형 폭은 sx로 준다.
                    width={"100px"}
                  >
                    <Text textStyle={"body-sm-bold"}>
                      {t("path_pick:filter.basis_bid")}
                    </Text>
                  </AppButton>
                  <AppButton
                    variant={"white"}
                    isActive={marketPriceBasis === "tradePrice"}
                    onClick={() => onChangeMarketPriceBasis("tradePrice")}
                    width={"100px"}
                  >
                    <Text textStyle={"body-sm-bold"}>
                      {t("path_pick:filter.basis_trade")}
                    </Text>
                  </AppButton>
                </Flex>
              )}
              {/* 비교 표시를 끄면 위 조건들이 사라지므로, URL에 남은 값도 함께 정리한다. */}
              <PathPickProductCardViewSettingsButton
                onComparisonHidden={resetComparisonFilters}
              />
              <AppSelect<PathPickProductOrderBy>
                name={t(
                  "path_pick:search_products.product_list.sort_recommended"
                )}
                value={filter.orderBy}
                options={sortOptions}
                isClearable={false}
                width={"150px"}
                onSelect={handleSelectOrderBy}
              />
            </Flex>
          </Flex>
          {/* 조건부 마운트하면 첫 오픈 때 suspense 쿼리(브랜드/카테고리)가 페이지 전체를
              suspend시키므로 항상 마운트하고, 접기/펼치기는 필터 내부에서 CSS로 처리한다.
              (적용된 필터 칩은 접힘 상태에서도 항상 노출) */}
          <PathPickSearchProductsProductListFilter
            isOpen={isFilterOpen}
            onClose={closeFilter}
          />
          {/* 목록 조작 줄 — 선택 액션만 둔다(표시·정렬 옵션은 상품 유형 줄 우측). */}
          <Flex gap={"8px"} alignItems={"center"}>
            <Flex gap={"8px"} alignItems={"center"}>
              <Flex gap={"16px"} alignItems={"center"}>
                <AppCheckBox
                  size={"small"}
                  isChecked={isAllSelected}
                  isIndeterminate={isIndeterminate}
                  isDisabled={!isPageSelectable}
                  onChange={() => onTogglePage()}
                  labelFontSize={"16px"}
                  name={t("path_pick:search_products.product_list.select_page")}
                />
                {canSelectAllResults && (
                  <PathPickSelectAllResultsCheckBox
                    count={allResultsCount}
                    isTruncated={isAllResultsTruncated}
                    isChecked={isAllResultsSelected}
                    isIndeterminate={isAllResultsIndeterminate}
                    isBusy={isFetchingAllResults}
                    onToggle={onToggleAllResults}
                  />
                )}
                {/* 모바일에서는 선택하면 뜨는 하단 플로팅바의 담기로 충분하고, 이 버튼이 자리를 잡으면
                    선택 체크박스 라벨이 두 줄로 접힌다. AppButton은 Tooltip으로 감싸여 있어 바깥 Box에서 분기한다. */}
                <Box display={{ base: "none", md: "block" }}>
                  <AppButton
                    variant={"white"}
                    isDisabled={selectedCount === 0}
                    onClick={onBulkPick}
                  >
                    <Flex gap={"4px"} alignItems={"center"}>
                      <CustomIcon
                        src={"package"}
                        width={16}
                        height={16}
                        color={selectedCount === 0 ? "#8F8F8C" : "#444440"}
                      />
                      <Text textStyle={"body-sm-bold"}>
                        {t("path_pick:search_products.product_list.pick")}
                      </Text>
                    </Flex>
                  </AppButton>
                </Box>
              </Flex>
            </Flex>
            {/*<Flex gap={"4px"}>*/}
            {/*  <AppButton*/}
            {/*    variant={"white"}*/}
            {/*    isActive={viewType === "list"}*/}
            {/*    onClick={() => onChangeViewType("list")}*/}
            {/*    chakraProps={{*/}
            {/*      gap: "4px",*/}
            {/*      padding: "4px 16px",*/}
            {/*    }}*/}
            {/*  >*/}
            {/*    <CustomIcon*/}
            {/*      src={"menu"}*/}
            {/*      width={16}*/}
            {/*      height={16}*/}
            {/*      color={viewType === "list" ? "#1272EF" : "#444440"}*/}
            {/*    />*/}
            {/*    <Text textStyle={"body-sm-bold"}>*/}
            {/*      {t("path_pick:search_products.product_list.view_list")}*/}
            {/*    </Text>*/}
            {/*  </AppButton>*/}
            {/*  <AppButton*/}
            {/*    variant={"white"}*/}
            {/*    isActive={viewType === "card"}*/}
            {/*    onClick={() => onChangeViewType("card")}*/}
            {/*    chakraProps={{*/}
            {/*      gap: "4px",*/}
            {/*      padding: "4px 16px",*/}
            {/*    }}*/}
            {/*  >*/}
            {/*    <CustomIcon*/}
            {/*      src={"copy"}*/}
            {/*      width={16}*/}
            {/*      height={16}*/}
            {/*      color={viewType === "card" ? "#1272EF" : "#444440"}*/}
            {/*    />*/}
            {/*    <Text textStyle={"body-sm-bold"}>*/}
            {/*      {t("path_pick:search_products.product_list.view_card")}*/}
            {/*    </Text>*/}
            {/*  </AppButton>*/}
            {/*</Flex>*/}
          </Flex>
        </Flex>
      </Flex>
      {isMobileFilterOpen && (
        <PathPickSearchProductsProductListFilterModal
          isOpen
          onClose={() => setIsMobileFilterOpen(false)}
        />
      )}
      {isKreamBuyerInfoModalOpen && (
        <PathPickSearchProductsKreamBuyerInfoModal
          isOpen
          onClose={() => setIsKreamBuyerInfoModalOpen(false)}
          value={kreamBuyerInfo}
          onSave={onSaveKreamBuyerInfo}
        />
      )}
    </>
  );
}

export default PathPickSearchProductsProductListHeader;
